import { clsx } from 'clsx';
import { KnownCookie } from 'common-types';
import { format } from 'date-fns';
import { useRouter } from 'next/router';
import React, { useMemo } from 'react';
import { useCookie } from 'react-use';
import { Divider } from 'ui';
import ItemDetailsLink from '~/components/Links/ItemDetailsLink';
import LockerLink from '~/components/Links/LockerLink';
import useFacetFiltering from '~/contexts/facetFiltering/useFacetFiltering';
import useRouteQuery from '~/hooks/useRouteQuery';
import CloseIcon from '~/public/static/svg/CloseIcon';
import filterStatePriceFormat from '~/utils/filterStatePriceFormat';

interface PillProps extends React.BaseHTMLAttributes<HTMLDivElement> {
  className?: string;
  onClear?: () => void;
  variant?: 'inverted';
}

const Pill: React.FC<PillProps> = ({
  children,
  className,
  onClear,
  variant,
  ...props
}) => (
  <div
    className={clsx('rounded border px-4 py-1 text-sm font-semibold', {
      'bg-mint-green-400 border-primary-500': !variant,
      'bg-primary-500 border-primary-500 text-white': variant === 'inverted',
    })}
    {...props}
  >
    <label className="flex items-center justify-center">
      <span className={className}>{children}</span>

      {onClear && (
        <button
          className="ml-3"
          onClick={onClear}
          type="button"
          aria-label="Clear filter"
        >
          <CloseIcon height="12" width="12" fill="currentColor" />
        </button>
      )}
    </label>
  </div>
);

interface Props {
  pipe?: boolean;
}

const PillContainer: React.FC<Props> = ({ pipe }) => {
  const {
    facets,
    filterState,
    similarToItem,
    unflattenedFilterState,
    changeFacetSelection,
    setPrices,
  } = useFacetFiltering();
  const router = useRouter();
  const { query } = useRouteQuery<any>();
  const [fastShipping, setFastShipping] = useCookie(
    'fast_shipping_filter_enabled' as KnownCookie,
  );

  const memoPills = useMemo(() => {
    const pills: React.ReactNode[] = [];
    const clear = (slug: string) => changeFacetSelection(slug, null);

    if (filterState.q) {
      pills.push(
        <Pill key="q" onClear={() => clear('q')}>
          &ldquo;{filterState.q}&rdquo;
        </Pill>,
      );
    }

    if (fastShipping === '1') {
      pills.push(
        <Pill
          key="fast_shipping"
          onClear={() => {
            setFastShipping('');
            router.reload();
          }}
        >
          Fast Delivery
        </Pill>,
      );
    }

    if (similarToItem) {
      pills.push(
        <Pill
          key="similar_to"
          className="max-w-48 truncate overflow-ellipsis"
          onClear={() => clear('similar_to')}
        >
          Related to:{' '}
          <ItemDetailsLink
            item={similarToItem}
            className="truncate"
            prefetch={false}
          >
            {similarToItem.name}
          </ItemDetailsLink>
        </Pill>,
      );
    }

    if (filterState.seller) {
      const seller = filterState.seller[0];

      pills.push(
        <Pill key={`seller-${seller}`} onClear={() => clear('seller')}>
          Seller: <LockerLink username={seller}>{seller}</LockerLink>
        </Pill>,
      );
    }

    // Matched At pills
    if (filterState.min_published_at) {
      pills.push(
        <Pill
          variant="inverted"
          key="min_published_at"
          onClear={() => clear('min_published_at')}
        >
          New since {format(new Date(filterState.min_published_at), 'MMM do')}
        </Pill>,
      );
    }

    // Price Drops
    if (filterState.price_drop) {
      pills.push(
        <Pill key="price_drop" onClear={() => clear('price_drop')}>
          Price Drops
        </Pill>,
      );
    }

    // Sold
    if (filterState.recently_sold) {
      pills.push(
        <Pill key="recently_sold" onClear={() => clear('recently_sold')}>
          Recently Sold
        </Pill>,
      );
    }

    // Reported
    if (filterState.reported) {
      pills.push(
        <Pill key="reported" onClear={() => clear('reported')}>
          Reported
        </Pill>,
      );
    }

    const priceText = filterStatePriceFormat(filterState);
    if (priceText) {
      pills.push(
        <Pill key="price" onClear={() => setPrices()}>
          {priceText}
        </Pill>,
      );
    }

    Object.entries(filterState).forEach(([key, value]) => {
      if (
        key === 'category' ||
        key === 'seller_option' ||
        !Array.isArray(value)
      ) {
        return;
      }

      const found = facets.find(f => f.slug === key);

      if (found && found.children) {
        found.children.forEach(child => {
          if (value.includes(child.id)) {
            pills.push(
              <Pill
                key={child.id}
                variant={key === 'sale' ? 'inverted' : undefined}
                onClear={() => changeFacetSelection(key, child)}
              >
                {child.name}
              </Pill>,
            );
          }
        });
      }
    });

    return pills;
  }, [
    filterState,
    similarToItem,
    query?.stid,
    changeFacetSelection,
    setPrices,
    facets,
  ]);

  if (memoPills.length === 0) {
    return null;
  }

  return (
    <div className={clsx('mb-4 flex flex-wrap gap-2')}>
      {memoPills}
      {pipe && <Divider vertical />}
    </div>
  );
};

export default PillContainer;
